@if (snapshot(); as snap) {

  <div class="d-flex flex-column gap-2">

    <mat-card appearance="outlined" class="ai-card">
      <app-card-header [displayName]="snap.block.display_name ?? snap.block.job_id">
        @switch (snap.run_status) {
          @case ('success') {
            <span class="notification-badge mat-font-label-sm notification-badge--success">
              <mat-icon>check_circle</mat-icon>Done
            </span>
          }
          @case ('error') {
            <span class="notification-badge mat-font-label-sm notification-badge--error">
              <mat-icon>error</mat-icon>Failed
            </span>
          }
          @case ('timeout') {
            <span class="notification-badge mat-font-label-sm notification-badge--error">
              <mat-icon>timer_off</mat-icon>Timeout
            </span>
          }
          @default {
            <span class="notification-badge mat-font-label-sm notification-badge--running">
              <mat-icon class="spin-icon">autorenew</mat-icon>Running
            </span>
          }
        }
      </app-card-header>

      <mat-card-content class="d-flex flex-column gap-3 px-3 pt-2 pb-3">

        <!-- Shared live-detail block for a running phase: pipeline_phase_status message,
             pipeline_phase_step sub-steps, deep_analysis activity, sub-agent todos.
             Reused by both the real phase loop and the pre-job_progress skeleton loop below,
             since discovery/deep_analysis can run to completion before job_progress ever
             reflects it (mid-run status now flows via pipeline_phase_status, not STATE_SNAPSHOT). -->
        <ng-template #phaseLiveDetail let-phaseName="phaseName">
          @let phaseMessage = getPhaseMessage(phaseName, snap);
          @if (phaseMessage) {
            <p class="mat-font-label-sm mat-text-on-surface-variant fst-italic mb-0 mt-1">{{ phaseMessage }}</p>
          }
          @let phaseSteps = getPhaseSteps(phaseName, snap);
          @if (phaseSteps.length > 0) {
            <div class="d-flex flex-column gap-1 ms-3 mt-1 ps-2 border-start border-secondary-subtle">
              @for (step of phaseSteps; track step.step) {
                <div class="d-flex align-items-center gap-2">
                  @switch (step.status) {
                    @case ('completed') {
                      <mat-icon class="mat-text-primary flex-shrink-0 mat-icon-sm">check_circle</mat-icon>
                    }
                    @case ('running') {
                      <span class="task-list-card__phase-spinner"></span>
                    }
                    @case ('error') {
                      <mat-icon class="mat-text-error flex-shrink-0 mat-icon-sm">error</mat-icon>
                    }
                    @default {
                      <mat-icon class="mat-text-on-surface-variant flex-shrink-0 mat-icon-sm">radio_button_unchecked</mat-icon>
                    }
                  }
                  <span class="mat-font-label-sm mat-text-on-surface-variant flex-fill">{{ step.label }}</span>
                  @if (step.status === 'error' && step.message) {
                    <span class="mat-text-error mat-font-label-sm">{{ step.message }}</span>
                  }
                </div>
              }
            </div>
          }
          @if (isDeepAnalysisPhase(phaseName) && snap.deepAnalysis.toolCall; as toolCall) {
            <div class="task-list-card__activity-row d-flex align-items-center gap-2 mt-2 mb-1 px-2 py-1 ms-3">
              <span class="task-list-card__phase-spinner"></span>
              <span class="mat-font-label-sm mat-text-on-surface-variant fw-semibold">{{ toolCall.label }}</span>
            </div>
          }
          @if (isDeepAnalysisPhase(phaseName) && snap.subAgentTodos.length > 0) {
            <div class="ms-3 mt-2 ps-2 border-start border-secondary-subtle">
              <app-todo-list [todos]="snap.subAgentTodos" [title]="snap.subAgentLabel ?? 'To-do list'" />
            </div>
          }
        </ng-template>

        <!-- Phase list -->
        @if (snap.progress?.phases?.length) {
          <div class="d-flex flex-column gap-1">
            @let phaseNames = getPhaseNamesFromProgress(snap);
            @for (phase of snap.progress!.phases; track phase.name; let i = $index) {
              @let rawStatus = getEffectivePhaseStatus(phase.name, phase.status, snap);
              @let effectiveStatus = (rawStatus === 'error' || rawStatus === 'skipped') ? rawStatus : (isPhaseForcedComplete(i, phaseNames, snap) ? 'completed' : rawStatus);
              <div class="d-flex align-items-start gap-2">
                @switch (effectiveStatus) {
                  @case ('completed') {
                    <mat-icon class="mat-text-primary flex-shrink-0">check_circle</mat-icon>
                  }
                  @case ('running') {
                    <span class="task-list-card__phase-spinner"></span>
                  }
                  @case ('warning') {
                    <mat-icon class="mat-text-on-surface-variant flex-shrink-0">warning</mat-icon>
                  }
                  @case ('error') {
                    <mat-icon class="mat-text-error flex-shrink-0">error</mat-icon>
                  }
                  @case ('skipped') {
                    <mat-icon class="mat-text-on-surface-variant flex-shrink-0" style="opacity: 0.5">not_interested</mat-icon>
                  }
                  @default {
                    <mat-icon class="mat-text-on-surface-variant flex-shrink-0"
                      [style.opacity]="isPhaseSkipped(phase, snap) ? '0.4' : null">
                      radio_button_unchecked
                    </mat-icon>
                  }
                }
                <div class="flex-fill">
                  <span class="mat-font-body-sm"
                    [class.mat-text-on-surface-variant]="isPhaseSkipped(phase, snap) || effectiveStatus === 'skipped'"
                    [style.opacity]="(isPhaseSkipped(phase, snap) || effectiveStatus === 'skipped') ? '0.5' : null">{{ phase.label }}</span>
                  @if (snap.run_status === 'success' && phase.status === 'completed') {
                    <span class="mat-text-on-surface-variant mat-font-label-sm ms-2">{{ formatPhaseDuration(phase.started_at, phase.completed_at) }}</span>
                    @if (phase.record_count !== null) {
                      <span class="mat-text-on-surface-variant mat-font-label-sm ms-1">· {{ phase.record_count }} records</span>
                    }
                  }
                  @if (phase.status === 'error' && phase.error_message) {
                    <p class="mat-font-label-sm mat-text-error mb-0 mt-1">{{ phase.error_message }}</p>
                  }
                  @if (effectiveStatus === 'running') {
                    <ng-container [ngTemplateOutlet]="phaseLiveDetail" [ngTemplateOutletContext]="{ phaseName: phase.name }" />
                  }
                  @if (debugMode.isDebugMode()) {
                    @for (provider of getPhaseProviders(phase.name); track $index) {
                      <div class="d-flex flex-column gap-1 ms-3 mt-1 ps-2 border-start border-secondary-subtle">
                        <div class="d-flex align-items-center gap-2">
                          <span class="mat-font-label-sm mat-text-on-surface-variant">{{ provider.name }}</span>
                          @if (provider.confidence !== undefined) {
                            <span class="mat-font-label-sm mat-text-primary">{{ (provider.confidence * 100).toFixed(0) }}%</span>
                          }
                          @if (provider.layer) {
                            <span class="mat-font-label-sm mat-text-on-surface-variant">· {{ provider.layer }}</span>
                          }
                        </div>
                        @if (provider.reasoning) {
                          <p class="mat-font-label-sm mat-text-on-surface-variant fst-italic mb-0">{{ provider.reasoning }}</p>
                        }
                      </div>
                    }
                    @let phaseStats = getPhaseStats(phase.name);
                    @if (phaseStats) {
                      <div class="d-flex align-items-center flex-wrap gap-2 ms-3 mt-1 ps-2 border-start border-secondary-subtle mat-font-label-sm mat-text-on-surface-variant">
                        <span>{{ phaseStats.ok }}/{{ phaseStats.total }} ok</span>
                        @if (phaseStats.failed > 0) {
                          <span class="mat-text-error">{{ phaseStats.failed }} failed</span>
                        }
                        @if (phaseStats.filtered > 0) {
                          <span>{{ phaseStats.filtered }} filtered</span>
                        }
                        <span>{{ (phaseStats.duration_ms / 1000).toFixed(1) }}s</span>
                      </div>
                    }
                  }
                </div>
              </div>
            }
          </div>
        } @else if (snap.block.expected_phases?.length) {
          <!-- Skeleton phase rows: progress.phases not populated yet (or absent). Live status/detail
               still comes from the phaseStates overlay (pipeline_phase_status) — job_progress.phases
               can lag an entire phase behind it (e.g. discovery can start+finish before its first
               STATE_SNAPSHOT with job_progress even arrives), so this must render live too, not just
               a static grey list. -->
          <div class="d-flex flex-column gap-1">
            @for (name of snap.block.expected_phases; track name; let i = $index) {
              @let rawStatus = getEffectivePhaseStatus(name, 'pending', snap);
              @let effectiveStatus = (rawStatus === 'error' || rawStatus === 'skipped') ? rawStatus : (isPhaseForcedComplete(i, snap.block.expected_phases!, snap) ? 'completed' : rawStatus);
              <div class="d-flex align-items-start gap-2" [class.task-list-card__skeleton-phase]="effectiveStatus === 'pending'">
                @switch (effectiveStatus) {
                  @case ('completed') {
                    <mat-icon class="mat-text-primary flex-shrink-0">check_circle</mat-icon>
                  }
                  @case ('running') {
                    <span class="task-list-card__phase-spinner"></span>
                  }
                  @case ('warning') {
                    <mat-icon class="mat-text-on-surface-variant flex-shrink-0">warning</mat-icon>
                  }
                  @case ('error') {
                    <mat-icon class="mat-text-error flex-shrink-0">error</mat-icon>
                  }
                  @case ('skipped') {
                    <mat-icon class="mat-text-on-surface-variant flex-shrink-0" style="opacity: 0.5">not_interested</mat-icon>
                  }
                  @default {
                    <mat-icon class="mat-text-on-surface-variant flex-shrink-0">radio_button_unchecked</mat-icon>
                  }
                }
                <div class="flex-fill">
                  <span class="mat-font-body-sm mat-text-on-surface-variant"
                    [style.opacity]="effectiveStatus === 'skipped' ? '0.5' : null">
                    {{ snap.block.expected_phase_labels[name] ?? name }}
                  </span>
                  @if (effectiveStatus === 'running') {
                    <ng-container [ngTemplateOutlet]="phaseLiveDetail" [ngTemplateOutletContext]="{ phaseName: name }" />
                  }
                </div>
              </div>
            }
          </div>
          @if (!isTerminal(snap)) {
            <mat-progress-bar mode="indeterminate" />
          }
        } @else if (isTerminal(snap)) {
          <!-- Terminal with no progress data and no expected phases: prevent empty card body -->
          <p class="mat-font-body-sm mat-text-on-surface-variant mb-0">No phase details available.</p>
        }

        <!-- Progress bar + elapsed (running) -->
        @if (!isTerminal(snap) && snap.progress; as progress) {
          <div class="d-flex flex-column gap-1">
            <mat-progress-bar mode="determinate"
              [value]="progress.phases_total > 0 ? (progress.phases_completed / progress.phases_total) * 100 : 0" />
            <div class="d-flex justify-content-between mat-font-label-sm mat-text-on-surface-variant">
              <span>{{ progress.phases_completed }} / {{ progress.phases_total }} phases</span>
              <span>{{ formatElapsed(elapsedSeconds()) }}</span>
            </div>
          </div>
        }

        <!-- Run-level error fallback -->
        @if (isTerminal(snap) && snap.run_status !== 'success' && snap.run_error_message && !hasPhaseError(snap)) {
          <p class="mat-font-body-sm mat-text-error mb-0">{{ snap.run_error_message }}</p>
        }

      </mat-card-content>

      <!-- Footer: View on map + Download data -->
      @if (canAddToMap() || addedToMap() || geojsonRefs().length > 0) {
        <div class="ai-card__footer px-2 py-1 d-flex align-items-center gap-2">
          @if (canAddToMap()) {
            <button matButton (click)="addAllToMap()" [disabled]="addingToMap()">
              <mat-icon>map</mat-icon>
              {{ addingToMap() ? 'Adding...' : 'View on map' }}
            </button>
          }
          @if (geojsonRefs().length > 0) {
            <button matButton (click)="downloadAllAsZip()" [disabled]="downloadingZip()">
              <mat-icon>download</mat-icon>
              {{ downloadingZip() ? 'Zipping...' : 'Download data' }}
            </button>
          }
          @if (addedToMap()) {
            <span class="d-flex align-items-center gap-1 mat-text-on-surface-variant mat-font-label-sm">
              <mat-icon>check_circle</mat-icon>Added to map
            </span>
          }
        </div>
      }
    </mat-card>

    <!-- Final result chips — always visible on success -->
    @if (snap.run_status === 'success' && visibleRefs().length > 0) {
      <div class="d-flex flex-wrap gap-2">
        @for (ref of visibleRefs(); track ref.ref_id) {
          <app-output-ref-chip [ref]="ref" />
        }
      </div>
    }

    <!-- Raw per-phase chips + debug info button — debug mode only -->
    @if (debugMode.isDebugMode()) {
      @if (snap.run_status === 'success' && rawRefs().length > 0) {
        <div class="d-flex flex-wrap gap-2">
          @for (ref of rawRefs(); track ref.ref_id) {
            <app-output-ref-chip [ref]="ref" />
          }
        </div>
      }
      @if (snap.state_debug) {
        <button mat-button class="d-flex align-items-center gap-1" (click)="showDebugInfo()">
          <mat-icon class="mat-icon-sm">bug_report</mat-icon>
          <span>Debug info</span>
        </button>
      }
    }

  </div>

} @else {

  <!-- Skeleton: before first poll — uses expected_phases from block for structure -->
  <mat-card appearance="outlined" class="ai-card">
    <app-card-header [displayName]="block().display_name">
      <span class="notification-badge mat-font-label-sm notification-badge--running">
        <mat-icon class="spin-icon">autorenew</mat-icon>Starting
      </span>
    </app-card-header>
    <mat-card-content class="d-flex flex-column gap-3 px-3 pt-2 pb-3">
      @if (block().expected_phases?.length) {
        <div class="d-flex flex-column gap-1">
          @for (name of block().expected_phases; track name) {
            <div class="d-flex align-items-start gap-2 task-list-card__skeleton-phase">
              <mat-icon class="mat-text-on-surface-variant flex-shrink-0">radio_button_unchecked</mat-icon>
              <span class="mat-font-body-sm mat-text-on-surface-variant">
                {{ block().expected_phase_labels[name] ?? name }}
              </span>
            </div>
          }
        </div>
      }
      <mat-progress-bar mode="indeterminate" />
    </mat-card-content>
  </mat-card>

}
